import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  CForm,
  CButton,
  P,
  Caption,
  CTextInput,
  CRow,
  CCol
} from "@appcues/component-library";
import MultiSelect from "components/Common/UI/MultiSelect";
import SelectProfileAttribute from "components/Common/SelectProfileAttribute";
import { useToggle } from "hooks/useToggle";
import { asPromised } from "utils/as-promised";
import { saveLocale } from "actions/account/locales";

import AttributeMultiSelect from "./AttributeMultiSelect";

export function AddLanguageForm({ languageOptions, onSave, locales }) {
  const [isDuplicate, setFormDuplicateError] = useState(false);
  const [isVisible, toggleForm] = useToggle(false);

  const handleSubmit = ({ name, property, values }) => {
    onSave({
      name,
      property,
      value: values.map(item => item.value).join("\n")
    }).then(() => {
      toggleForm();
    });
  };

  const checkForDuplicates = (name, val, setFieldValue) => {
    const dupe = locales.some(
      element => val.toLowerCase() === element.name.toLowerCase()
    );
    setFormDuplicateError(dupe);
    return setFieldValue(name, val);
  };

  const options = useMemo(
    () =>
      languageOptions.map(lang => {
        return {
          value: lang.code,
          label: lang.desc
        };
      }),
    [languageOptions]
  );

  return (
    <CRow fullWidth center="xs" marginTop="45px">
      {isVisible ? (
        <CForm
          onSubmit={handleSubmit}
          initialValues={{
            name: "",
            property: "_lastBrowserLanguage",
            values: []
          }}
        >
          {({ values, setFieldValue }) => (
            <CForm.Element justifyContent="space-between" flexDirection="row">
              <CRow top="xs">
                <CCol sm={12} lg>
                  <CTextInput
                    className="testid-language-name-input"
                    name="name"
                    onChange={val =>
                      checkForDuplicates("name", val, setFieldValue)
                    }
                    value={values.name}
                    marginRight={15}
                    marginBottom={10}
                    placeholder="Language name"
                  />
                  {isDuplicate && (
                    <Caption color="red" className="testid-error-message">
                      Language already exists!
                    </Caption>
                  )}
                </CCol>
                <CCol xs sm={4} lg>
                  <SelectProfileAttribute
                    className="testid-property-attribute-input"
                    marginBottom={10}
                    value={values.property}
                    onChange={val => setFieldValue("property", val.value)}
                  />
                </CCol>
                <CCol xs sm={2} lg={1}>
                  <P textAlign="center" marginTop={5} marginBottom={5}>
                    is one of
                  </P>
                </CCol>
                <CCol xs sm={4} lg>
                  {values.property === "_lastBrowserLanguage" ? (
                    <MultiSelect
                      className="testid-browser-lang-input"
                      name="values"
                      options={options}
                      allowCustomValues={false}
                      onChange={val => setFieldValue("values", val)}
                      placeholder="Browser Language"
                    />
                  ) : (
                    <AttributeMultiSelect
                      property={values.property}
                      onChange={val => setFieldValue("values", val)}
                    />
                  )}
                </CCol>
                <CCol sm={12} lg>
                  <CButton onClick={toggleForm} marginRight={8}>
                    Cancel
                  </CButton>
                  <CForm.SubmitButton
                    className="testid-save-button"
                    type="positive"
                    isDisabled={isDuplicate || values.values.length <= 0}
                  >
                    Save
                  </CForm.SubmitButton>
                </CCol>
              </CRow>
            </CForm.Element>
          )}
        </CForm>
      ) : (
        <CButton
          className="testid-toggle-button"
          onClick={toggleForm}
          width={140}
        >
          Add language
        </CButton>
      )}
    </CRow>
  );
}

AddLanguageForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  languageOptions: PropTypes.arrayOf(
    PropTypes.shape({ desc: PropTypes.string, code: PropTypes.string })
  ).isRequired
};

const mapDispatchToProps = dispatch => ({
  onSave: values => asPromised(dispatch, saveLocale(values))
});

export default connect(null, mapDispatchToProps)(AddLanguageForm);
